import React, { memo, useCallback, useMemo, useRef, Fragment } from 'react';
import { generateUUID } from '@Sage/ssm-utils';
import { Icon } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import useOnboarding from '../../hooks/useOnboarding';
import { Modal, Table, ReadOnly, EditForm, AddForm } from '../';
import { types, TABLES } from '../../../constants';

const SegmentAccounts = ({ getList, setList, handleError, handleSetCount }) => {
    const modalInputRef = useRef(null);
    const { t } = useWeb5Context();
    const {
        state,
        dispatch,
        handleDragEnter,
        handleDragEnd,
        handleDelete,
        handleEdit,
        handleEditFormChange: onEditFormChange,
        handleEditFormBlur,
        handleAddFormChange: onAddFormChange,
        handleAddFormBlur,
        handleUpdate,
        formHasErrors,
        isRequired,
        validateAddForm,
    } = useOnboarding({
        getData: () => getList(TABLES.ACCOUNT_TYPES),
        getDataCallback: (data) => data.sort((a, b) => a.order - b.order),
        handleError,
        handleSetCount: (value) => handleSetCount(TABLES.ACCOUNT_TYPES, value),
        setData: (data) => setList(TABLES.ACCOUNT_TYPES, data),
        parseData: (data) => data.map((item, index) => ({ ...item, order: index })),
    });

    const formSchema = useMemo(() => {
        return [
            {
                label: t('label_account_type'),
                placeholder: t('placeholder_text_field'),
                labelMode: 'vertical',
                name: 'name',
                type: 'text',
                isFullWidth: true,
                isRequired: true,
                ref: (ref) => (modalInputRef.current = ref),
            },
        ];
    }, [t]);

    const validateField = useCallback(
        (field, value) => {
            let error = null;

            switch (field) {
                case 'name':
                    error = isRequired(value);
                    break;
                default:
                    break;
            }

            return error;
        },
        [isRequired],
    );

    const handleAddFormChange = useCallback(
        (payload) => onAddFormChange(payload, validateField, formSchema),
        [onAddFormChange, validateField, formSchema],
    );

    const handleAddFormSave = useCallback(() => {
        const form = state.modal[types.MODAL_ADD];
        const addedTypes = form.values;
        const currentTypes = state.data;

        if (!validateAddForm(addedTypes, validateField, formSchema)) return;

        let newTypes = addedTypes.reduce(
            (arr, item) => [
                ...arr,
                {
                    id: generateUUID(),
                    temp: true,
                    name: item.name,
                    readOnly: false,
                },
            ],
            [...currentTypes],
        );

        handleUpdate(newTypes);
        dispatch({ type: types.CLOSE_MODAL });
    }, [
        state.modal,
        state.data,
        validateAddForm,
        validateField,
        formSchema,
        handleUpdate,
        dispatch,
    ]);

    const handleEditFormChange = useCallback(
        (payload) => onEditFormChange(payload, validateField),
        [onEditFormChange, validateField],
    );

    const handleEditFormSave = useCallback(() => {
        const values = state.modal[types.MODAL_EDIT].values;
        const currentValues = state.data;
        const newValues = currentValues.reduce((arr, item) => {
            let newValue;

            if (values.id === item.id) {
                newValue = {
                    ...item,
                    ...values,
                };
            } else {
                newValue = item;
            }

            return [...arr, newValue];
        }, []);

        handleUpdate(newValues);
        dispatch({ type: types.CLOSE_MODAL });
    }, [state.modal, state.data, handleUpdate, dispatch]);

    const tableProps = useMemo(() => {
        return {
            title: t('label_accont_type_title'),
            description: t('label_accont_type_title_desc'),
            actionText: t('action_add_account_type'),
            actionOnClick: () => dispatch({ type: types.OPEN_MODAL, value: types.MODAL_ADD }),
            components: {
                ReadOnly,
            },
            columnDefs: [
                {
                    colId: 'name',
                    headerName: t('label_account_types'),
                    field: 'name',
                    suppressSizeToFit: false,
                    resizable: false,
                    rowDrag: true,
                },
                {
                    resizable: false,
                    cellRenderer: 'ReadOnly',
                    suppressSizeToFit: true,
                },
            ],
            rowData: state.data,
            actions: [
                (row) => {
                    return !row.readOnly
                        ? {
                              icon: (
                                  <Icon
                                      name="edit"
                                      onClick={() =>
                                          handleEdit({
                                              id: row.id,
                                              name: row.name,
                                          })
                                      }
                                  />
                              ),
                              tooltip: { content: t('action_edit') },
                          }
                        : null;
                },

                (row) => {
                    return !row.readOnly
                        ? {
                              icon: <Icon name="trash" onClick={() => handleDelete(row)} />,
                              tooltip: { content: t('action_delete') },
                          }
                        : null;
                },
            ],
            loading: state.loading,
            useDragRows: true,
            getRowNodeId: (row) => row.id,
            onDragEnter: handleDragEnter,
            onDragEnd: handleDragEnd,
            forceActionsRefresh: true,
        };
    }, [
        t,
        state.data,
        state.loading,
        handleDragEnter,
        handleDragEnd,
        dispatch,
        handleEdit,
        handleDelete,
    ]);

    const modalProps = useMemo(() => {
        let customProps = {};

        const commonProps = {
            isOpen: state.modal.isOpen,
            onRequestClose: () => dispatch({ type: types.CLOSE_MODAL }),
            onCancel: () => dispatch({ type: types.CLOSE_MODAL }),
            modalInputRef: modalInputRef,
        };

        switch (state.modal.type) {
            case types.MODAL_EDIT:
                customProps = {
                    title: t('labe_edit_entity', [t('label_account_type')]),
                    onConfirm: handleEditFormSave,
                    isConfirmDisabled: formHasErrors(types.MODAL_EDIT),
                };
                break;
            case types.MODAL_ADD:
                customProps = {
                    title: t('label_accont_type_title'),
                    description: t('label_account_types_modal_desc'),
                    onConfirm: handleAddFormSave,
                    isConfirmDisabled: formHasErrors(types.MODAL_ADD),
                };
                break;
            default:
                break;
        }

        return {
            ...commonProps,
            ...customProps,
        };
    }, [
        dispatch,
        formHasErrors,
        handleAddFormSave,
        handleEditFormSave,
        state.modal.isOpen,
        state.modal.type,
        t,
    ]);

    const modalContent = useMemo(() => {
        let modalContentProps = null;
        let ModalContent = null;

        switch (state.modal.type) {
            case types.MODAL_EDIT:
                modalContentProps = {
                    form: state.modal[types.MODAL_EDIT],
                    onChange: handleEditFormChange,
                    onBlur: handleEditFormBlur,
                    schema: formSchema,
                    modalInputRef,
                };
                ModalContent = EditForm;
                break;
            case types.MODAL_ADD:
                modalContentProps = {
                    form: state.modal[types.MODAL_ADD],
                    onChange: handleAddFormChange,
                    onBlur: handleAddFormBlur,
                    schema: formSchema,
                    modalInputRef,
                };
                ModalContent = AddForm;
                break;
            default:
                break;
        }

        if (!ModalContent) return null;

        return <ModalContent {...modalContentProps} />;
    }, [
        state,
        handleEditFormChange,
        handleEditFormBlur,
        handleAddFormChange,
        handleAddFormBlur,
        formSchema,
    ]);

    return (
        <Fragment>
            <Table {...tableProps} />
            <Modal {...modalProps}>{modalContent}</Modal>
        </Fragment>
    );
};

export default memo(SegmentAccounts);
